import React, {FC, ReactNode, useCallback, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useDebounce} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ErrorWithData, ExtendedError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {reportToBugsnag} from '../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  HttpNetworkError,
  HttpTimeoutError,
} from '../../../../../libs/shared/core/src/services/http/httpError';
import {
  InterfaceEventsEnum,
  SchedulingLinksAccessPointsEnum,
  SchedulingSectionsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {CalendarsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {
  ChromelessButton,
  ChromelessButtonProps,
} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Loader, LoaderVariantsEnum} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {
  buildInvalidQuestionOptionUids,
  SettingsSectionsEnum,
} from '../../../../helpers/models/schedulingLinkHelpers';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkMeetingPropertiesModel,
  SchedulingLinkModel,
} from '../../../../models/api/schedulingLinkModel';
import {SchedulingLinkQuestionModel} from '../../../../models/api/schedulingLinkQuestionModel';
import {CalendarsPanelState} from '../../../../store/panels/calendars/calendarsPanelState';
import {updateMeetingPropertiesAction} from '../../../../store/panels/calendars/scheduling/calendarsPanelSchedulingActions';
import {Frame} from '../../../common/frames/frame';
import {FrameTitle} from '../../../common/frames/frameTitle';
import {SchedulingLinkAppearanceSettings} from './schedulingLinkAppearanceSettings';
import {SchedulingLinkAvailabilitySettingsContainer} from './schedulingLinkAvailabilitySettingsContainer';
import {SchedulingLinkMeetingSettingsContainer} from './schedulingLinkMeetingSettingsContainer';
import {SchedulingLinkSettingsCloseConfirm} from './schedulingLinkSettingsCloseConfirm';
import {schedulingLinkSettingsValidators} from './schedulingLinkSettingsValidators';
import {SchedulingLinkUrlSettingsContainer} from './schedulingLinkUrlSettingsContainer';

/*
 * Props.
 */

export interface SchedulingLinkSettingsDialogPublicProps {
  panelId: CalendarsPanelId;
  schedulingLinkId?: number;
  onDialogClose: () => void;
  accessPoint: SchedulingLinksAccessPointsEnum;
}

interface SchedulingLinkSettingsDialogProps extends SchedulingLinkSettingsDialogPublicProps {
  didEditDraft: boolean;
  draftLink: DraftSchedulingLinkModel;
  status: AsyncStatusesEnum;
  otherSlugs: ReadonlyArray<string>;
  onUpdateSchedulingLink: (arg: Partial<DraftSchedulingLinkModel>) => void;
  onSaveClick: () => Promise<SchedulingLinkModel>;
  shouldForcePopout: boolean;
  isPopout: boolean;
  onOpenPopout: () => void;
  schedulingPanel: CalendarsPanelState;
}

/*
 * Constants.
 */

const sectionTitleTextRenderer: {[T in SettingsSectionsEnum]: ReactNode} = {
  [SettingsSectionsEnum.AVAILABILITY]: (
    <Trans id="schedulinglink-settings-section-availability">Availability</Trans>
  ),
  [SettingsSectionsEnum.MEETING]: (
    <Trans id="schedulinglink-settings-section-meeting-title">Meeting settings</Trans>
  ),
  [SettingsSectionsEnum.URL]: <Trans id="schedulinglink-settings-section-url">Link &amp; URL</Trans>,
  [SettingsSectionsEnum.APPEARANCE]: (
    <Trans id="schedulinglink-settings-section-appearance">Appearance</Trans>
  ),
};

const debounceDelay = 100;

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  flex-grow: 1;

  display: grid;
  grid-template-areas:
    'sidebar section'
    'footer footer';
  grid-template-columns: 1fr 3fr;
  grid-column-gap: 40px;
  grid-template-rows: minmax(0, 1fr) auto;
  overflow: auto;
  padding-top: 26px;
`;

const StyledDialogSectionContent = styled.div`
  grid-area: section;
  padding: 0px 40px 0px 16px;
  overflow: auto;
`;

type SectionButtonStyleProps = ChromelessButtonProps & {$isSelected: boolean};
const StyledSectionButton = styled(ChromelessButton)<SectionButtonStyleProps>`
  color: ${(p) => (p.$isSelected ? p.theme.greys.shade80 : p.theme.greys.shade60)};
  font-weight: ${(p) => (p.$isSelected ? fontWeights.medium : fontWeights.normal)};
  &:hover {
    color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledSidebarDiv = styled.div`
  grid-area: sidebar;
  display: grid;
  grid-row-gap: 15px;
  grid-auto-rows: max-content;
  margin-left: 24px;
`;

const StyledFooterDiv = styled.div`
  grid-area: footer;
`;

const StyledSeparatorDiv = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.greys.shade30};
`;

const StyledDialogButtonGroupWrapperDiv = styled.div`
  height: 68px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-right: 20px;
`;

const StyledStatusDiv = styled.div`
  width: 100%;
  height: 100%;
  ${center()}
`;

/*
 * Component.
 */

export const SchedulingLinkSettingsDialog: FC<SchedulingLinkSettingsDialogProps> = (props) => {
  const {accessPoint, onUpdateSchedulingLink, schedulingLinkId, shouldForcePopout, isPopout, onOpenPopout} =
    props;
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);
  const [selectedSection, setSelectedSection] = useState(SettingsSectionsEnum.AVAILABILITY);
  const sectionContentRef = useRef<HTMLDivElement>(null);

  const onSelectSection = (section: SettingsSectionsEnum, shouldScrollToTop: boolean = true) => {
    setSelectedSection(section);
    if (sectionContentRef.current && shouldScrollToTop) {
      sectionContentRef.current.scrollTop = 0;
    }
  };

  // Meeting properties
  const dispatch = useAppDispatch();
  const onUpdateMeetingProperties = useCallback(
    (changes: Partial<SchedulingLinkMeetingPropertiesModel>) =>
      dispatch(updateMeetingPropertiesAction(props.panelId, changes)),
    [dispatch, props.panelId],
  );
  const onSummaryChangeDebounced = useDebounce(
    (summary) => onUpdateMeetingProperties({summary}),
    debounceDelay,
  );
  const onLocationChangeDebounced = useDebounce(
    (location) => onUpdateMeetingProperties({location}),
    debounceDelay,
  );
  const onDescriptionChangeDebounced = useDebounce(
    (description) => onUpdateMeetingProperties({description}),
    debounceDelay,
  );
  const onQuestionsChangeDebounced = useDebounce(
    (questions) => onUpdateMeetingProperties({questions}),
    debounceDelay,
  );

  // Scheduling link properties
  const onNameChangeDebounced = useDebounce((name) => onUpdateSchedulingLink({name}), debounceDelay);
  const onSlugChangeDebounced = useDebounce((slug) => onUpdateSchedulingLink({slug}), debounceDelay);
  const onGreetingChangeDebounced = useDebounce(
    (greeting) => onUpdateSchedulingLink({greeting}),
    debounceDelay,
  );
  const onDisplayNameChangeDebounced = useDebounce(
    (displayName) => onUpdateSchedulingLink({displayName}),
    debounceDelay,
  );

  // Input validation
  const [hasNoSelectedTimes, setHasNoSelectedTimes] = useState(false);
  const [invalidQuestionOptionUids, setInvalidQuestionOptionUids] = useState<ReadonlyArray<string>>([]);

  // Dialog footer button handlers
  const onCancelClick = () => (props.didEditDraft ? setIsConfirmationOpen(true) : props.onDialogClose());
  const onSaveClick = async () => {
    onSummaryChangeDebounced.flush();
    onLocationChangeDebounced.flush();
    onDescriptionChangeDebounced.flush();
    onQuestionsChangeDebounced.flush();
    onNameChangeDebounced.flush();
    onSlugChangeDebounced.flush();
    onGreetingChangeDebounced.flush();
    onDisplayNameChangeDebounced.flush();

    const invalidSections = collectEnumValues(SettingsSectionsEnum).filter((section) => {
      const validationData = {draftLink: props.draftLink, otherSlugs: props.otherSlugs};
      const validator = schedulingLinkSettingsValidators[section];
      return !validator(validationData);
    });

    // When a section has an invalid field value, skip the save and navigate to that section.
    const [invalidSection] = invalidSections;
    if (invalidSection && invalidSection === SettingsSectionsEnum.AVAILABILITY) {
      setHasNoSelectedTimes(true);
    }
    if (invalidSection && invalidSection === SettingsSectionsEnum.MEETING) {
      setInvalidQuestionOptionUids(
        buildInvalidQuestionOptionUids(props.draftLink.meetingProperties.questions),
      );
    }
    if (invalidSection) {
      onSelectSection(invalidSection, invalidSection !== SettingsSectionsEnum.MEETING);
      return;
    }

    try {
      const newSchedulingLink = await props.onSaveClick();

      trackSave(Boolean(schedulingLinkId), accessPoint, newSchedulingLink);

      props.onDialogClose();
    } catch (error) {
      if (error instanceof HttpNetworkError || error instanceof HttpTimeoutError) {
        return;
      }

      // TODO: graceful handling. set a state variable with the error type
      // and render a message to the user.
      reportToBugsnag(new ExtendedError('Failed to save scheduling link.', error));
    }
  };

  // Under the "Prevent Double Booking" section, it's possible that the user has selected calendars without CalendarVersions.
  // This usually means the calendars haven't been connected to Front yet.
  // We should disable the save button in this case.
  const hasCalendarsWithoutCalendarVersion = props.schedulingPanel.viewingNowCalendars.some(
    (calendar) => !calendar.calendarVersionId,
  );
  const saveButtonTooltip = hasCalendarsWithoutCalendarVersion
    ? () => (
        <Trans id="schedulinglink-setting-save-disabled-tooltip">
          One or more of the calendars to prevent double booking for is not connected to Front. Please remove
          them.
        </Trans>
      )
    : undefined;

  const editTitle = <Trans id="schedulinglink-settings-edit-title">Edit scheduling link</Trans>;
  const newTitle = <Trans id="schedulinglink-settings-new-title">New scheduling link</Trans>;
  const title = schedulingLinkId ? editTitle : newTitle;
  return (
    <Frame
      onRequestClose={(forceClose) => {
        // Close the dialog without confirmation if we didn't edit or this is a force close.
        if (forceClose || !props.didEditDraft) {
          props.onDialogClose();
        } else {
          setIsConfirmationOpen(true);
        }
      }}
      width={isPopout ? 900 : 580}
      height={isPopout ? 700 : 540}
      shouldForcePopout={shouldForcePopout}
      onOpenPopout={onOpenPopout}
    >
      <FrameTitle>{title}</FrameTitle>
      <StyledWrapperDiv>
        {renderSidebar(selectedSection, onSelectSection)}
        <StyledDialogSectionContent ref={sectionContentRef}>
          {renderSection(
            props,
            selectedSection,
            hasNoSelectedTimes,
            setHasNoSelectedTimes,
            invalidQuestionOptionUids,
            setInvalidQuestionOptionUids,
            onUpdateMeetingProperties,
            onSummaryChangeDebounced,
            onLocationChangeDebounced,
            onDescriptionChangeDebounced,
            onQuestionsChangeDebounced,
            onNameChangeDebounced,
            onSlugChangeDebounced,
            onGreetingChangeDebounced,
            onDisplayNameChangeDebounced,
          )}
        </StyledDialogSectionContent>
        {renderFooter(onCancelClick, onSaveClick, hasCalendarsWithoutCalendarVersion, saveButtonTooltip)}
      </StyledWrapperDiv>
      {isConfirmationOpen && renderCloseConfirmation(props, () => setIsConfirmationOpen(false))}
    </Frame>
  );
};

function renderSidebar(
  selectedSection: SettingsSectionsEnum,
  onSelectSection: (arg: SettingsSectionsEnum) => void,
) {
  const sections = [
    SettingsSectionsEnum.AVAILABILITY,
    SettingsSectionsEnum.MEETING,
    SettingsSectionsEnum.URL,
    SettingsSectionsEnum.APPEARANCE,
  ];
  return (
    <StyledSidebarDiv>
      {sections.map((section) =>
        renderSectionButtonRow(section, selectedSection === section, () => onSelectSection(section)),
      )}
    </StyledSidebarDiv>
  );
}

function renderSectionButtonRow(section: SettingsSectionsEnum, isSelected: boolean, onClick: () => void) {
  return (
    <div key={section}>
      <StyledSectionButton onClick={onClick} $isSelected={isSelected}>
        {sectionTitleTextRenderer[section]}
      </StyledSectionButton>
    </div>
  );
}

function renderSection(
  props: SchedulingLinkSettingsDialogProps,
  selectedSection: SettingsSectionsEnum,
  hasNoSelectedTimes: boolean,
  setHasNoSelectedTimes: (arg: boolean) => void,
  invalidQuestionOptionUids: ReadonlyArray<string>,
  setInvalidQuestionOptionUids: (arg: ReadonlyArray<string>) => void,
  onUpdateMeetingProperties: (arg: Partial<SchedulingLinkMeetingPropertiesModel>) => void,
  onSummaryChange: (arg: string) => void,
  onLocationChange: (arg: string) => void,
  onDescriptionChange: (arg: string) => void,
  onQuestionsChange: (arg: ReadonlyArray<SchedulingLinkQuestionModel>) => void,
  onNameChange: (arg: string) => void,
  onSlugChange: (arg: string) => void,
  onGreetingChange: (arg: string) => void,
  onDisplayNameChange: (arg: string) => void,
) {
  if (props.status !== AsyncStatusesEnum.LOADED) {
    return renderLoader(props.status);
  }

  switch (selectedSection) {
    case SettingsSectionsEnum.AVAILABILITY:
      return (
        <SchedulingLinkAvailabilitySettingsContainer
          panelId={props.panelId}
          showNoSelectedTimesError={hasNoSelectedTimes}
          clearNoSelectedTimesError={() => setHasNoSelectedTimes(false)}
          draftLink={props.draftLink}
          onUpdateMeetingProperties={onUpdateMeetingProperties}
          onUpdateSchedulingLink={props.onUpdateSchedulingLink}
        />
      );

    case SettingsSectionsEnum.MEETING:
      return (
        <SchedulingLinkMeetingSettingsContainer
          panelId={props.panelId}
          onSummaryChange={onSummaryChange}
          onDurationMinutesChange={(durationMinutes) => onUpdateMeetingProperties({durationMinutes})}
          onConferenceChange={(conferenceType) => onUpdateMeetingProperties({conferenceType})}
          onLocationChange={onLocationChange}
          onDescriptionChange={onDescriptionChange}
          onQuestionsChange={onQuestionsChange}
          invalidQuestionOptionUids={invalidQuestionOptionUids}
          setInvalidQuestionOptionUids={setInvalidQuestionOptionUids}
          isEditing={Boolean(props.schedulingLinkId)}
        />
      );

    case SettingsSectionsEnum.URL:
      return (
        <SchedulingLinkUrlSettingsContainer
          panelId={props.panelId}
          onNameChange={onNameChange}
          onSlugChange={onSlugChange}
        />
      );

    case SettingsSectionsEnum.APPEARANCE:
      return (
        <SchedulingLinkAppearanceSettings
          color={props.draftLink.color}
          greeting={props.draftLink.greeting}
          displayName={props.draftLink.displayName}
          onGreetingChange={onGreetingChange}
          onDisplayNameChange={onDisplayNameChange}
          onColorChange={(color) => props.onUpdateSchedulingLink({color})}
        />
      );

    default:
      reportToBugsnag(
        new ErrorWithData('Tried to render unsupported schedulingLink settings section', {
          section: selectedSection,
        }),
      );
      return null;
  }
}

function renderFooter(
  onCancelClick: () => void,
  onSaveClick: () => void,
  isDisabled: boolean,
  renderTooltip: (() => ReactNode) | undefined,
) {
  return (
    <StyledFooterDiv>
      <StyledSeparatorDiv />
      <StyledDialogButtonGroupWrapperDiv>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={onCancelClick}>
            <Trans id="schedulinglink-settings-cancel">Cancel</Trans>
          </Button>
          <Button
            onClick={onSaveClick}
            isDisabled={isDisabled}
            renderTooltip={renderTooltip}
            variant={ButtonVariantsEnum.PRIMARY}
          >
            <Trans id="schedulinglink-settings-save">Save</Trans>
          </Button>
        </ButtonGroup>
      </StyledDialogButtonGroupWrapperDiv>
    </StyledFooterDiv>
  );
}

function renderCloseConfirmation(props: SchedulingLinkSettingsDialogProps, onCancelClick: () => void) {
  return (
    <SchedulingLinkSettingsCloseConfirm onConfirmClick={props.onDialogClose} onCancelClick={onCancelClick} />
  );
}

function renderLoader(status: AsyncStatusesEnum) {
  if (status === AsyncStatusesEnum.LOADING) {
    return (
      <StyledStatusDiv>
        <Loader size={VisualSizesEnum.LARGE} variant={LoaderVariantsEnum.BASE} />
      </StyledStatusDiv>
    );
  }

  return (
    <StyledStatusDiv>
      <Trans id="schedulinglink-settings-loaderror">Unable to load scheduling link settings.</Trans>
    </StyledStatusDiv>
  );
}

function trackSave(
  isUpdate: boolean,
  accessPoint: SchedulingLinksAccessPointsEnum,
  newSchedulingLink: SchedulingLinkModel,
) {
  const eventData = {
    scheduling_link_id: newSchedulingLink.id,
    type: SchedulingSectionsEnum.SCHEDULING_LINK,
  };

  if (isUpdate && accessPoint === SchedulingLinksAccessPointsEnum.P4) {
    trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_P4_UPDATE, eventData);
    return;
  }

  if (!isUpdate && accessPoint === SchedulingLinksAccessPointsEnum.P4) {
    trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_P4_CREATE, eventData);
    return;
  }

  if (isUpdate && accessPoint === SchedulingLinksAccessPointsEnum.COMPOSER) {
    trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_COMPOSER_UPDATE, eventData);
    return;
  }

  if (!isUpdate && accessPoint === SchedulingLinksAccessPointsEnum.COMPOSER) {
    trackInterfaceEvent(InterfaceEventsEnum.SCHEDULING_COMPOSER_CREATE, eventData);
  }
}
